import {EX} from "@/exclaim";
import {addCsrfTokenToXhr} from "@/network/CsrfTokenManagement";
import {getServerRequestErrorMessage} from "@/network/RequestUtils";
import {Validators} from "@/validation/Validators";
import {notSet} from "@/utils/ValueUtils";
import jQuery from "jquery";

const $ = jQuery;

export function commonAjaxBeforeSend(
    this: unknown,
    jqXHR: JQueryXHR,
    settings?: JQuery.AjaxSettings,
): void {
    addCsrfTokenToXhr(jqXHR, settings?.type);
}

export function alertP(message: string, title?: string): Promise<void> {
    return new Promise((resolve) => {
        alertDialog(message, title, () => { resolve(); });
    });
}

/**
 * @deprecated Use `alertDialog()` instead
 */
export function alert(message: string, title?: string, callback?: () => void): void {
    alertDialog(message, title, callback);
}

// FUTURE: This function could be moved to somewhere more generic
export function alertDialog(message: string, title?: string, callback?: () => void): void {
    const buttons: JQueryUI.DialogButtonOptions[] = [
        {
            text: EX.Messages.prop("Button_Ok"),
            click: function (_event: Event) {
                $(this).dialog("close");
                if (typeof callback === "function") {
                    callback();
                }
            },
        },
    ];
    $("<div></div>", {
        dialog: {
            buttons: buttons,
            close: function (_event: Event, _ui: JQueryUI.DialogUIParams) {
                $(this).remove();
                if (typeof callback === "function") {
                    callback();
                }
            },
            resizable: false,
            title: title,
            modal: true,
            width: 400, // default is 300, so making it a bit wider as we have some long text in places
        },
        css: {"white-space": "pre-line"},
        addClass: "exclaim-jquery-dialog",
        text: message,
    });
}

export function confirm(
    message: string,
    title?: string,
    buttons?: JQueryUI.DialogButtonOptions[],
    options?: JQueryUI.DialogOptions | null,
    callback?: (() => void) | null,
    defaultButtonIndex?: number,
): void {
    const defaultButtons: JQueryUI.DialogButtonOptions[] = [
        {
            text: EX.Messages.prop("Button_Ok"),
            click: function () {
                $(this).dialog("close");
            },
        },
    ];
    const dialogOptions: JQueryUI.DialogOptions = $.extend(options || {}, {
        buttons: buttons || defaultButtons,
        close: function (_event: Event, _ui: JQueryUI.DialogUIParams) {
            $(this).remove();
            if (typeof callback === "function") {
                callback();
            }
        },
        title: title,
        resizable: false,
        width: 400, // default is 300, so making it a bit wider as we have some long text in places
        modal: true,
        open: function (_event: Event, _ui: JQueryUI.DialogUIParams) {
            // Only try to change the input focus if we have been provided
            // with a defaultButtonIndex (that is also non-zero, as zero
            // would be the default anyway)
            if (defaultButtonIndex) {
                // WARNING: This may change with different versions of jQueryUI
                // noinspection SpellCheckingInspection
                const $button = $(this).siblings('.ui-dialog-buttonpane').find('button:eq(' + defaultButtonIndex + ')');
                if ($button.length === 0) {
                    console.warn(
                        "Invalid value for defaultButtonIndex (%d), couldn't find a button at that index",
                        defaultButtonIndex,
                    );
                } else {
                    $button.trigger("focus");
                }
            }
        },
    });

    $("").dialog({});
    $("<div></div>", {
        dialog: dialogOptions,
        css: {"white-space": "pre-line"},
        addClass: "exclaim-jquery-dialog",
        html: message,
    });
}

export function confirmDialogP(
    message: string,
    title: string,
    buttonYes: string,
    buttonNo: string,
    defaultButtonIndex?: number,
): Promise<void> {
    return new Promise((resolve, reject) =>
        confirmDialog(
            message,
            title,
            buttonYes,
            buttonNo,
            () => { resolve(); },
            () => { reject(new Error("Dialog cancelled")); },
            defaultButtonIndex,
        )
    );
}

function buildYesNoButtons(
    buttonYes: string,
    buttonNo: string,
    callbackYes?: () => void,
    callbackNo?: () => void,
): JQueryUI.DialogButtonOptions[] {
    return [
        {
            text: buttonYes,
            click: function () {
                $(this).dialog("close");
                if (typeof callbackYes === "function") {
                    callbackYes();
                }
            },
        },
        {
            text: buttonNo,
            click: function () {
                $(this).dialog("close");
                if (typeof callbackNo === "function") {
                    callbackNo();
                }
            },
        },
    ];
}

export function confirmDialog(
    message: string,
    title: string,
    buttonYes: string,
    buttonNo: string,
    callbackYes?: () => void,
    callbackNo?: () => void,
    defaultButtonIndex?: number,
): void {
    const buttons = buildYesNoButtons(buttonYes, buttonNo, callbackYes, callbackNo);
    confirm(message, title, buttons, null, null, defaultButtonIndex);
}

// FUTURE: migrate to a more flexible function with an options structure
export function confirmDialogWithCancel( //NOSONAR
    message: string,
    title: string,
    buttonYes: string,
    buttonNo: string,
    buttonCancel: string,
    callbackYes?: () => void,
    callbackNo?: () => void,
    callbackCancel?: () => void,
    defaultButtonIndex?: number,
): void {
    const buttons = buildYesNoButtons(buttonYes, buttonNo, callbackYes, callbackNo);
    buttons.push({
        text: buttonCancel,
        click: function () {
            $(this).dialog("close");
            if (typeof callbackCancel === "function") {
                callbackCancel();
            }
        },
    });
    confirm(message, title, buttons, null, null, defaultButtonIndex);
}

export function getCookie(cname: string): string {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (const c of ca) {
        const ct = c.trimStart();
        if (ct.startsWith(name)) {
            return ct.substring(name.length, ct.length);
        }
    }
    return "";
}

export function alertError(errorMessage: string, callback?: () => void): void {
    alertDialog(errorMessage, EX.Messages.prop("Dialog_error_title"), callback);
}

export function genericAjaxOnFail(
    jqXHR: JQueryXHR,
    textStatus: JQuery.Ajax.ErrorTextStatus,
    _errorThrown?: string,
    callback?: () => void,
): void {
    const now = new Date();
    const msg = getAjaxFailErrorMessage(jqXHR, textStatus) +
        '\n\n' + now.toISOString();
    alertDialog(
        msg,
        EX.Messages.prop("Dialog_error_title"),
        callback,
    );
}

function getJsonObjectFromResponse(jqXHR: JQueryXHR): unknown {
    if (notSet(jqXHR)) {
        return undefined;
    }
    // responseType and response are not declared in @types/jquery but are present at runtime
    const xhr = jqXHR as JQueryXHR & {responseType?: string; response?: unknown};
    if (jqXHR.responseJSON || (xhr.responseType === "json" && xhr.response)) {
        return jqXHR.responseJSON || xhr.response;
    }
    return undefined;
}

export function getAjaxFailErrorMessage(
    jqXHR: JQueryXHR,
    textStatus: JQuery.Ajax.ErrorTextStatus,
): string {
    return getServerRequestErrorMessage(
        jqXHR.status,
        textStatus,
        jqXHR.responseText,
        getJsonObjectFromResponse(jqXHR),
    );
}

export function validateFileName(fileName: string): boolean {
    return fileName.trim().length > 0 && Validators.expressions.FILE_NAME.exec(fileName) === null;
}

export function isAllArraysOfEqualLength(...arrays: unknown[]): boolean {
    let l: number | null = null;
    for (const array of arrays) {
        if (!Array.isArray(array)) {
            return false;
        }
        l = l ?? array.length;
        if (l !== array.length) {
            return false;
        }
    }
    return true;
}

export function sortByPropertyFn<T>(
    prop: {name: keyof T, reverse?: boolean},
): (a: T, b: T) => 0 | 1 | -1 {
    return function (a: T, b: T): 0 | 1 | -1 {
        const name = prop.name;
        const reverse = prop.reverse;
        if (a[name] < b[name]) {
            return reverse ? 1 : -1;
        }
        if (a[name] > b[name]) {
            return reverse ? -1 : 1;
        }
        return 0;
    };
}

export function registerJQueryExtensions(): void {

    $.fn.isVisible = function () {
        return this.length > 0 &&
                this.css('visibility') !== 'hidden' &&
                this.css('display') !== 'none';
    };

    /**
     * This function returns events registered by jQuery
     */
    $.fn.getEvents = function () {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (typeof (jQuery as any)._data === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (jQuery as any)._data(this.get(0), 'events') || {};
        } else if (typeof (this.data) === 'function') { // jQuery version < 1.7.?
            return this.data('events') || {};
        }
        return {};
    };

    /**
     * Add an event binding *before* any pre-existing bindings.
     */
    $.fn.addEventListenerBefore = function (
        type: string,
        fn?: (e: JQuery.Event) => void,
    ) {
        if (!fn) {
            return this;
        }
        this.each(function () {
            // Add the new event 'at the bottom'
            $(this).on(type, null, null, fn);

            const handlers = $(this).getEvents()[type];

            // Only one handler.
            if (handlers.length === 1) {
                return; // Continue
            }

            // Move last handler to first index.
            handlers.splice(0, 0, handlers.pop());
        });
        return this;
    };

    $.fn.disable = function () {
        const $elements = $(this).find("input, select, textarea, button").addBack();
        $elements.prop({
            "readonly": true,
            "disabled": true,
        });
        $elements.addClass("readOnlyClass");
        return this;
    };

    $.fn.enable = function () {
        const $elements = $(this).find("input, select, textarea, button").addBack();
        $elements.prop({
            "readonly": false,
            "disabled": false,
        });
        $elements.removeClass("readOnlyClass");
        return this;
    };

    $.ajaxSetup({
        beforeSend: commonAjaxBeforeSend,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($ as any).propHooks.disabled = {
        set: function (el: HTMLElement, value: boolean) {
            $(el).toggleClass("readOnlyClass", value);
        },
    };

    // Sizzle passes match array m as 4th argument; typing is too narrow in @types/sizzle
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($.expr.pseudos as any).contains = function (a: HTMLElement, _i: number, m: string[]) {
        return (
            a.textContent ||
            (a as HTMLElement & {innerText: string}).innerText ||
            ""
        ).toUpperCase().includes(m[3].toUpperCase());
    };

}
